import { timer as observableTimer, Observable, Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';

import { UserService } from '../../../services/users/user.service';

declare var window: any;

@Component({
  selector: 'app-user-show',
  templateUrl: './user-show.component.html',
  styleUrls: ['./user-show.component.css'],
})
export class UserShowComponent implements OnInit, OnDestroy {
  favorites: Array<any> = [];
  recents: Array<any> = [];
  mattersLoading: boolean = true;
  mattersLoadError: boolean = false;

  time_str: string;
  greeting: string = '';
  userFirstName: string = '';

  timer: Observable<number>;

  timerSubscription: Subscription | null;
  weatherSubscription: Subscription | null;
  componentDestroyed: boolean = false;

  userShowData: any = {
    stats: {
      active_matters: 0,
      todays_events: 0,
      pending_reminders: 0
    }
  };

  // Weather radar map - centered on Chester County, PA area
  radarMapUrl: SafeResourceUrl;

  // Weather data from API
  weatherData: any = null;
  weatherLoading: boolean = true;
  weatherError: boolean = false;

  weatherConfig: any = {
    cityid: '5201756',
    appid: '7adbe9d3c5475dc322c580a9ee51c172',
    latitude: 40.40,
    longitude: -75.59,
    units: 'imperial',
  };
  private readonly requestTimeoutMs: number = 20000;
  private readonly weatherTimeoutMs: number = 5000;

  constructor(
    private userService: UserService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {
    // Default radar URL - Bally, PA area - will be updated with user's location
    this.setRadarUrl(40.40, -75.59);
  }

  setRadarUrl(lat: number, lng: number) {
    // Windy embed - more reliable than RainViewer
    // zoom=9 for closer view, centered on user location
    const radarUrl = `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lng}&detailLat=${lat}&detailLon=${lng}&width=300&height=300&zoom=9&level=surface&overlay=radar&product=radar&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=mph&metricTemp=%C2%B0F&radarRange=-1`;
    this.radarMapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(radarUrl);
  }

  // Component lifecycle events
  ngOnInit() {
    this.updateGreeting();
    this.userService.getUserShowData().then((response_json) => {
      if (this.componentDestroyed) return;
      this.time_str = this.formatDate(new Date());
      this.timer = observableTimer(1000, 1000);
      if (!response_json) return;
      // Merge response with defaults to preserve stats structure
      this.userShowData = {
        ...this.userShowData,
        ...response_json,
        stats: {
          ...this.userShowData.stats,
          ...(response_json.stats || {})
        }
      };
      this.userFirstName = response_json.user_first_name || response_json.user_login;
      // Update radar map with user's location
      if (response_json.latitude && response_json.longitude) {
        this.setRadarUrl(response_json.latitude, response_json.longitude);
        this.weatherConfig.latitude = response_json.latitude;
        this.weatherConfig.longitude = response_json.longitude;
      }
      if (response_json.appid) this.weatherConfig.appid = response_json.appid;
      if (response_json.cityid) this.weatherConfig.cityid = response_json.cityid;
      this.subscribeToTimer();
      this.fetchWeatherData();
      this.userService.navbarComponent.setHomeMenu(response_json.user_login, response_json.user_id, response_json.contact_id);
    });
    this.loadUserMatters();
  }

  loadUserMatters() {
    this.mattersLoading = true;
    this.mattersLoadError = false;
    this.withTimeout(this.userService.getUserMatters(), this.requestTimeoutMs).then((response_json) => {
      if (this.componentDestroyed) return;
      console.log('getUserMatters response:', response_json);
      this.mattersLoading = false;
      if (response_json) {
        this.favorites = response_json.favorites || [];
        this.recents = response_json.recents || [];
        console.log('Favorites loaded:', this.favorites.length, 'Recents loaded:', this.recents.length);
      }
    }).catch((err) => {
      if (this.componentDestroyed) return;
      console.error('Error loading user matters:', err);
      this.mattersLoading = false;
      this.mattersLoadError = true;
    });
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutHandle: any;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutHandle = setTimeout(() => reject(new Error('Request timed out')), timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).then((response: T) => {
      clearTimeout(timeoutHandle);
      return response;
    }).catch((error) => {
      clearTimeout(timeoutHandle);
      throw error;
    });
  }

  ngOnDestroy() {
    this.componentDestroyed = true;
    this.clearTimerSubscription();
    if (this.weatherSubscription) {
      this.weatherSubscription.unsubscribe();
      this.weatherSubscription = null;
    }
  }

  // Component methods
  clearTimerSubscription() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  formatDate(date: Date): string {
    let result: string = '';
    switch (date.getDay()) {
      case 0:
        result += 'Sunday, ';
        break;
      case 1:
        result += 'Monday, ';
        break;
      case 2:
        result += 'Tuesday, ';
        break;
      case 3:
        result += 'Wednesday, ';
        break;
      case 4:
        result += 'Thursday, ';
        break;
      case 5:
        result += 'Friday, ';
        break;
      case 6:
        result += 'Saturday, ';
        break;
    }
    switch (date.getMonth()) {
      case 0:
        result += 'January ';
        break;
      case 1:
        result += 'February ';
        break;
      case 2:
        result += 'March ';
        break;
      case 3:
        result += 'April ';
        break;
      case 4:
        result += 'May ';
        break;
      case 5:
        result += 'June ';
        break;
      case 6:
        result += 'July ';
        break;
      case 7:
        result += 'August ';
        break;
      case 8:
        result += 'September ';
        break;
      case 9:
        result += 'October ';
        break;
      case 10:
        result += 'November ';
        break;
      case 11:
        result += 'December ';
        break;
    }
    result += `${date.getDate() < 10 ? '0' : ''}${date.getDate()}, ${date.getFullYear()}`;
    return result;
  }

  subscribeToTimer() {
    this.timerSubscription = this.timer.subscribe((timesCalled) => (this.time_str = this.formatDate(new Date())));
  }
  openNewEvent() {
    this.router.navigateByUrl('/events?new_event=true');
  }

  updateGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) {
      this.greeting = 'Good morning';
    } else if (hour < 17) {
      this.greeting = 'Good afternoon';
    } else {
      this.greeting = 'Good evening';
    }
  }

  navigateToMatters() {
    this.router.navigateByUrl('/matters');
  }

  navigateToEvents() {
    this.router.navigateByUrl('/events');
  }

  onRemindersDismissed(count: number) {
    if (this.userShowData.stats.pending_reminders > 0) {
      this.userShowData.stats.pending_reminders = Math.max(0, this.userShowData.stats.pending_reminders - count);
    }
  }

  fetchWeatherData() {
    if (this.componentDestroyed) return;
    const url = this.weatherApiUrl();
    this.weatherLoading = true;
    this.weatherError = false;

    if (this.weatherSubscription) this.weatherSubscription.unsubscribe();
    this.weatherSubscription = this.http.get(url).pipe(timeout(this.weatherTimeoutMs)).subscribe(
      (data: any) => {
        if (this.componentDestroyed) return;
        this.weatherData = data;
        this.weatherLoading = false;
      },
      (error) => {
        if (this.componentDestroyed) return;
        console.error('Weather API error:', error);
        this.weatherError = true;
        this.weatherLoading = false;
      }
    );
  }

  weatherApiUrl(): string {
    const hasLatitude = this.weatherConfig.latitude !== null && this.weatherConfig.latitude !== undefined && this.weatherConfig.latitude !== '';
    const hasLongitude = this.weatherConfig.longitude !== null && this.weatherConfig.longitude !== undefined && this.weatherConfig.longitude !== '';
    const lat = Number(this.weatherConfig.latitude);
    const lng = Number(this.weatherConfig.longitude);
    const locationQuery = hasLatitude && hasLongitude && Number.isFinite(lat) && Number.isFinite(lng)
      ? `lat=${lat}&lon=${lng}`
      : `id=${this.weatherConfig.cityid}`;

    return `https://api.openweathermap.org/data/2.5/weather?${locationQuery}&appid=${this.weatherConfig.appid}&units=${this.weatherConfig.units}`;
  }

  getWeatherIconUrl(iconCode: string): string {
    return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  }
}
